import React from 'react';
import { CheckIcon } from 'lucide-react';
type Tome = {
  number: string;
  title: string;
  subtitle: string;
  body: string;
  promise: string;
  flag?: string;
};
const tomes: Tome[] = [{
  number: '01',
  title: 'The Foundations',
  subtitle: 'Deep Diagnostic & Onboarding',
  body: 'Turn your first session into instant trust and long-term retention. A word-for-word Session Zero script, the 5-Element WOW onboarding experience, a 4-layer deep diagnostic, and the 3-phase client roadmap builder — plus 5 ready worksheets.',
  promise: 'Never improvise a first session again.'
}, {
  number: '02',
  title: 'The Core',
  subtitle: 'Mindset Rewiring & Breakthroughs',
  body: 'Shift a limiting belief in a single session. The 7-Step Reframe Protocol, the 7 most common limiting beliefs, identity-level coaching frameworks, and 12 ready-to-run mindset session scripts.',
  promise: 'For when the client is stuck in their own head.'
}, {
  number: '03',
  title: 'The Momentum',
  subtitle: 'Action Architecture & Habit Engineering',
  body: 'Turn insight into daily action. The 90-Day Action Blueprint, the 4-Pillar Action Architecture, the 6 root causes of procrastination, the Plateau Protocol, and 12 high-momentum session scripts.',
  promise: 'Where transformation actually becomes real.',
  flag: '⭐ Most used'
}, {
  number: '04',
  title: 'The Emotional Intelligence Layer',
  subtitle: 'Emotional Intelligence & Resilience',
  body: 'Build clients who don’t collapse when life gets hard. The 5-Competency EI framework, a stress & regulation toolkit, the Fear Navigation Framework, and the full Resilience Architecture System.',
  promise: 'For the sessions that get emotional.'
}, {
  number: '05',
  title: 'The Communication Blueprint',
  subtitle: 'Communication, Relationships & Social Mastery',
  body: 'Make the inner transformation visible in every relationship. The NVC & DESC frameworks, the 4 communication styles, the 5-step boundary process, and the difficult-conversation navigation map.',
  promise: 'So the change shows up in real life.'
}, {
  number: '06',
  title: 'The Renewal System',
  subtitle: 'Closing, Integration & The Coaching Legacy',
  body: 'Close powerfully, renew ethically, grow your practice. The 5-dimension Transformation Audit (so the client sees their progress), the complete closing protocol, the ethical renewal conversation system, and referral & testimonial tools.',
  promise: 'The end isn’t a goodbye — it’s a graduation.',
  flag: '🏆 Practice growth'
}];
const includes = ['Complete, ready-to-run sessions', 'Printable + digital worksheets', 'Word-for-word scripts', 'Client-facing deliverables', 'Aligned with professional (ICF) coaching standards'];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-205" data-mp="2m5p1ieudpdb9">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-id="element-206" data-mp="2h5u9u92s4jp5">
        <h2 id="tomes-heading" className="font-serif font-bold text-ink text-2xl sm:text-4xl text-center" data-id="element-207" data-mp="wn0c99quene6">
          The 6 Tomes
        </h2>
        <p className="mt-3 text-center text-ink-soft max-w-2xl mx-auto" data-id="element-208" data-mp="16koh4si5xba2">
          One tome for every phase of the client journey — open it, pick the
          session, run it.
        </p>

        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-209" data-mp="1ajxahw9aj753">
          {tomes.map(tome => <li key={tome.number} className="flex flex-col rounded-2xl bg-white border border-cream-dark shadow-card p-6" data-id="element-210" data-mp="iuu4oc1r9oqc">
              <div className="flex items-center justify-between gap-3" data-id="element-211" data-mp="mvdyp6r18c89">
                <span className="font-serif font-bold text-sm text-sage tracking-[0.14em] uppercase" data-id="element-212" data-mp="ro77c5pmg4pm">
                  Tome {tome.number}
                </span>
                {tome.flag && <span className="rounded-full bg-terracotta-soft text-terracotta text-[10px] font-bold uppercase tracking-wider px-2.5 py-1" data-id="element-213" data-mp="3vbfmmxypw0t4">
                    {tome.flag}
                  </span>}
              </div>

              <h3 className="mt-3 font-serif font-bold text-xl sm:text-2xl text-ink leading-snug" data-id="element-214" data-mp="376ogb2nub74o">
                {tome.title}
              </h3>
              <p className="mt-1 italic text-sm text-ink-muted" data-id="element-215" data-mp="3h6oy2k629f6r">
                {tome.subtitle}
              </p>

              <p className="mt-4 text-[15px] leading-relaxed text-ink-soft flex-grow" data-id="element-216" data-mp="3ux27kqylp1y7">
                {tome.body}
              </p>

              <p className="mt-5 pt-4 border-t border-cream-dark font-semibold text-ink" data-id="element-217" data-mp="3q1d80nmkzl1w">
                {tome.promise}
              </p>
            </li>)}
        </ul>

        <div className="mt-10 rounded-2xl bg-sage-tint border border-sage-soft p-6 sm:p-7" data-id="element-218" data-mp="3slr280y9raf">
          <p className="font-serif font-bold text-ink text-lg text-center" data-id="element-219" data-mp="2aj1wc47ydh84">
            What every tome includes
          </p>
          <ul className="mt-5 flex flex-wrap justify-center gap-3" data-id="element-220" data-mp="25lrz42ebo19q">
            {includes.map(item => <li key={item} className="inline-flex items-center gap-2 rounded-full bg-white border border-sage-soft px-3.5 py-2 text-xs sm:text-sm font-medium text-ink" data-id="element-221" data-mp="q0hn6o1exk0q">
                <CheckIcon className="w-4 h-4 text-vital" aria-hidden="true" data-id="element-222" data-mp="ga4sw43sqche" />
                {item}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}